import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import api from '../lib/apiClient';
import { applyThemeColors } from '../utils/colorShades';
import { applyFavicon } from '../utils/favicon';

// Matches the actual fields returned by GET /settings (SettingController::show
// -> the Setting model's own JSON serialization) — see
// api/app/Models/Setting.php and api/app/Http/Controllers/SettingController.php.
// Falah's ConfigContext.jsx also carried mosque_*/prayer_calc_* defaults;
// those don't exist on VNPF's Setting model (no mosque/prayer-times feature
// here) and have been dropped rather than carried over as dead fields.
export interface Settings {
  currency: string;
  timezone: string;
  app_name: string;
  app_icon_url: string | null;
  theme_primary_color: string;
  theme_accent_color: string;
  org_name: string | null;
  org_registration_number: string | null;
  org_tax_id: string | null;
  org_phone: string | null;
  org_email: string | null;
  org_address_line1: string | null;
  org_address_line2: string | null;
  org_city: string | null;
  org_state: string | null;
  org_postal_code: string | null;
  org_country: string | null;
  org_logo_url: string | null;
  procurement_quotation_threshold: number;
  procurement_payment_approval_threshold: number;
  receipt_comment: string | null;
  service_desk_url: string | null;
  prefix_quotations: string;
  prefix_invoices: string;
  prefix_receipts: string;
  prefix_requests: string;
  prefix_orders: string;
  prefix_payments: string;
  prefix_system_id: string;
  prefix_contribution_receipts: string;
  prefix_employees: string;
  prefix_positions: string;
  prefix_assets: string;
  prefix_investments: string;
  working_days_per_month: number;
  timesheet_approval_levels: number;
  leave_attachment_required_days: number | null;
  payroll_requires_attendance: boolean;
  staff_contribution_employer_id: number | null;
  leave_liability_chart_of_account_id: number | null;
  min_employee_contribution_rate: number;
  min_employer_contribution_rate: number;
  contribution_payment_due_days: number;
  retirement_account_percentage: number;
  investment_account_percentage: number;
  medisave_account_percentage: number;
  penalty_rate_type: string;
  penalty_rate_value: number;
  penalty_grace_period_days: number;
  penalty_payment_due_days: number;
  penalty_waiver_escalation_threshold: number;
  penalty_notice_delivery_method: string;
  penalty_auto_issue_notice: boolean;
  penalty_income_chart_of_account_id: number | null;
  prefix_penalty_notices: string;
  prefix_penalty_receipts: string;
  benefit_min_retirement_age: number;
  benefit_min_membership_months: number;
  benefit_cheque_dual_signature_threshold: number;
  benefit_withdrawal_chart_of_account_id: number | null;
  benefit_death_chart_of_account_id: number | null;
  benefit_suspense_chart_of_account_id: number | null;
  prefix_benefit_claims: string;
  prefix_benefit_cheques: string;
  prefix_benefit_batches: string;
  gl_cash_bank_chart_of_account_id: number | null;
  prefix_gl_batches: string;
  prefix_journal_entries: string;
  ppe_accounting_policy_note: string | null;
  mobile_phone_pattern: string;
  send_portal_login_email: boolean;
  google_sso_enabled: boolean;
  google_client_id: string | null;
  google_client_secret_set: boolean;
  google_redirect_uri: string;
  microsoft_sso_enabled: boolean;
  microsoft_client_id: string | null;
  microsoft_client_secret_set: boolean;
  microsoft_redirect_uri: string;
  resend_enabled: boolean;
  resend_api_key_set: boolean;
  resend_api_key_set_at: string | null;
  sms_to_enabled: boolean;
  sms_to_api_key_set: boolean;
  sms_to_api_key_set_at: string | null;
  sms_to_api_key_set_by: string | null;
  sms_to_sender_id_set: boolean;
  sms_to_sender_id_set_at: string | null;
  sms_to_sender_id_set_by: string | null;
}

const DEFAULT_SETTINGS: Settings = {
  currency: 'VUV',
  timezone: 'UTC',
  app_name: 'VNPF One',
  app_icon_url: null,
  theme_primary_color: '#3f7a29',
  theme_accent_color: '#f8991c',
  org_name: null,
  org_registration_number: null,
  org_tax_id: null,
  org_phone: null,
  org_email: null,
  org_address_line1: null,
  org_address_line2: null,
  org_city: null,
  org_state: null,
  org_postal_code: null,
  org_country: null,
  org_logo_url: null,
  procurement_quotation_threshold: 6000,
  procurement_payment_approval_threshold: 500000,
  receipt_comment: null,
  service_desk_url: null,
  prefix_quotations: 'RQ',
  prefix_invoices: 'RI',
  prefix_receipts: 'RR',
  prefix_requests: 'PR',
  prefix_orders: 'PO',
  prefix_payments: 'PP',
  prefix_system_id: 'U',
  prefix_contribution_receipts: 'CR',
  prefix_employees: 'EMP',
  prefix_positions: 'POS',
  prefix_assets: 'AST',
  prefix_investments: 'INV',
  working_days_per_month: 21.75,
  timesheet_approval_levels: 1,
  leave_attachment_required_days: null,
  payroll_requires_attendance: false,
  staff_contribution_employer_id: null,
  leave_liability_chart_of_account_id: null,
  min_employee_contribution_rate: 4,
  min_employer_contribution_rate: 6,
  contribution_payment_due_days: 0,
  retirement_account_percentage: 50,
  investment_account_percentage: 25,
  medisave_account_percentage: 25,
  penalty_rate_type: 'percentage_per_month',
  penalty_rate_value: 0,
  penalty_grace_period_days: 0,
  penalty_payment_due_days: 30,
  penalty_waiver_escalation_threshold: 0,
  penalty_notice_delivery_method: 'portal',
  penalty_auto_issue_notice: false,
  penalty_income_chart_of_account_id: null,
  prefix_penalty_notices: 'PN',
  prefix_penalty_receipts: 'PY',
  benefit_min_retirement_age: 0,
  benefit_min_membership_months: 0,
  benefit_cheque_dual_signature_threshold: 0,
  benefit_withdrawal_chart_of_account_id: null,
  benefit_death_chart_of_account_id: null,
  benefit_suspense_chart_of_account_id: null,
  prefix_benefit_claims: 'BC',
  prefix_benefit_cheques: 'CHQ',
  prefix_benefit_batches: 'EFT',
  gl_cash_bank_chart_of_account_id: null,
  prefix_gl_batches: 'GLB',
  prefix_journal_entries: 'JE',
  ppe_accounting_policy_note: null,
  mobile_phone_pattern: '^[0-9]{8}$',
  send_portal_login_email: true,
  google_sso_enabled: false,
  google_client_id: null,
  google_client_secret_set: false,
  google_redirect_uri: '',
  microsoft_sso_enabled: false,
  microsoft_client_id: null,
  microsoft_client_secret_set: false,
  microsoft_redirect_uri: '',
  resend_enabled: false,
  resend_api_key_set: false,
  resend_api_key_set_at: null,
  sms_to_enabled: false,
  sms_to_api_key_set: false,
  sms_to_api_key_set_at: null,
  sms_to_api_key_set_by: null,
  sms_to_sender_id_set: false,
  sms_to_sender_id_set_at: null,
  sms_to_sender_id_set_by: null,
};

// GET /list-items returns every row across every list_key — grouped here by
// list_key so callers can look up e.g. lists['asset_category'] directly.
export interface ListItem {
  id: number;
  list_key: string;
  value: string;
  label: string;
  sort_order: number;
}

export type ListsByKey = Record<string, ListItem[]>;

interface ConfigContextValue {
  settings: Settings;
  lists: ListsByKey;
  loaded: boolean;
  reload: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

function groupByListKey(items: ListItem[]): ListsByKey {
  return items.reduce<ListsByKey>((acc, item) => {
    (acc[item.list_key] ??= []).push(item);
    return acc;
  }, {});
}

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [lists, setLists] = useState<ListsByKey>({});
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    const [settingsRes, listsRes] = await Promise.all([
      api.get<Settings>('/settings'),
      api.get<ListItem[]>('/list-items'),
    ]);
    setSettings(settingsRes.data);
    setLists(groupByListKey(listsRes.data));
    setLoaded(true);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    applyThemeColors(settings.theme_primary_color, settings.theme_accent_color);
  }, [settings.theme_primary_color, settings.theme_accent_color]);

  useEffect(() => {
    applyFavicon(settings.app_icon_url);
  }, [settings.app_icon_url]);

  return (
    <ConfigContext.Provider value={{ settings, lists, loaded, reload }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig(): ConfigContextValue {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export function listOptions(lists: ListsByKey, key: string): ListItem[] {
  return lists[key] ?? [];
}

export function labelFor(lists: ListsByKey, key: string, value: string): string {
  return lists[key]?.find((item) => item.value === value)?.label ?? value;
}
